import React from 'react';
// icons
import { IconContext } from 'react-icons';
import {
    Redirect,
    Route,
    BrowserRouter as Router,
    Switch,
} from 'react-router-dom';

import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import PrivateRoute from './components/auth/PrivateRoute';
import SideNav from './components/nav/SideNav';
// Components
import styled from 'styled-components';

// Constants
import aampeTheme from './constants/aampeTheme';
import routes from './constants/routes';

import { useAuthRedirect } from './hooks/useAuthRedirect';
// Views
import { useAuthSession } from './hooks/useAuthSession';

import envConfig from './services/env-config';

import AampeAdminMain from './views/aampeAdmin/AampeAdminMain';
import ConfigureMainLazy from './views/configure/ConfigureMain.lazy';
import DocsLogin from './views/docs/DocsLogin';
import FormulasMain from './views/formulaCatalog/FormulasMain';
import HomeDashboardV2 from './views/home/HomeDashboardV2';
import LoginMain from './views/login/LoginMain';
import FormulaSequencesMainLazy from './views/message-sequencing/FormulaSequencesMainLazy';
import MessageSettingsLazy from './views/messageSettings/MessageSettingsMain.lazy';
import MonitoringMainLazy from './views/monitoring/MonitoringMain.lazy';
import TopicManagementMainLazy from './views/monitoring/topicManagement/TopicManagementMain.lazy';
import LabelManagementMainRouteLazy from './views/personalizationManager/LabelManagementMainRoute.lazy';
import PluginAuth from './views/plugin/PluginAuth';
import SamplesLibraryMainLazy from './views/samplesLibrary/InspirationLibraryMain.lazy';
import SnippetsMainLazy from './views/snippets/SnippetsMain.lazy';
import SurfaceMainLazy from './views/surface/SurfaceMain.lazy';

export const APP_CONTAINER_ID = 'app-container';

interface AppWithSessionProps {
    getUserConfirmation: (
        message: string,
        callback: (confirmed: boolean) => void,
    ) => void;
    redirectedRoutes: React.ReactNode[];
}

export default function AppWithSession({
    getUserConfirmation,
    redirectedRoutes,
}: AppWithSessionProps) {
    const { isAuthenticated, status: authStatus } = useAuthSession();
    useAuthRedirect({ isAuthenticated, status: authStatus });
    const authorizing = authStatus === 'loading';

    return (
        <>
            <StyledBackdrop open={authorizing}>
                {authorizing ? <CircularProgress color="inherit" /> : null}
            </StyledBackdrop>
            <IconContext.Provider
                value={{ size: aampeTheme.size.icon.default }}
            >
                <Router
                    basename={envConfig.BASE_PATH}
                    // getUserConfirmation is fired only on pages where Prompt is used
                    getUserConfirmation={getUserConfirmation}
                >
                    <StyledAppContainer>
                        <div id="modal-root" />
                        <div id="snackbar-root" />
                        {isAuthenticated && <SideNav />}

                        <StyledContentContainer id={APP_CONTAINER_ID}>
                            <Switch>
                                {/* Redirects have grown unwieldy so they have been put in their own separate file */}
                                {redirectedRoutes}

                                <Route path={routes.auth}>
                                    <LoginMain />
                                </Route>
                                <PrivateRoute
                                    path={routes.dashboard}
                                    component={HomeDashboardV2}
                                />
                                <PrivateRoute
                                    path={routes.formulasMain}
                                    component={FormulasMain}
                                />
                                <PrivateRoute
                                    exact
                                    path={routes.inspiration}
                                    component={SamplesLibraryMainLazy}
                                />
                                <PrivateRoute
                                    path={routes.surface}
                                    component={SurfaceMainLazy}
                                />
                                <PrivateRoute
                                    path={routes.genAI}
                                    component={SnippetsMainLazy}
                                />
                                {/* collections */}
                                <PrivateRoute
                                    path={routes.monitoringMain}
                                    component={MonitoringMainLazy}
                                />
                                <PrivateRoute
                                    path={routes.labelManagement}
                                    component={LabelManagementMainRouteLazy}
                                />
                                <PrivateRoute
                                    path={routes.topicManagement}
                                    component={TopicManagementMainLazy}
                                />
                                <PrivateRoute
                                    path={routes.messageSettings}
                                    component={MessageSettingsLazy}
                                />
                                <PrivateRoute
                                    path={routes.systemSetupMain}
                                    component={ConfigureMainLazy}
                                />
                                <PrivateRoute
                                    path={routes.messageSequencing}
                                    component={FormulaSequencesMainLazy}
                                />
                                <PrivateRoute
                                    path={routes.docsLogin}
                                    component={DocsLogin}
                                />
                                <PrivateRoute
                                    path={routes.pluginAuth}
                                    component={PluginAuth}
                                />
                                <PrivateRoute
                                    path={routes.aampeAdmin}
                                    component={AampeAdminMain}
                                />

                                <Redirect to={routes.dashboard} />
                            </Switch>
                        </StyledContentContainer>
                    </StyledAppContainer>
                </Router>
            </IconContext.Provider>
        </>
    );
}

const StyledBackdrop = styled(Backdrop)({
    zIndex: 9999,
    color: '#fff',
    width: '100%',
});

const StyledAppContainer = styled.div({
    display: 'flex',
    height: '100vh',
    flexFlow: 'row nowrap',
    justifyContent: 'flex-start',
    width: '100%',
});

const StyledContentContainer = styled.div({
    display: 'flex',
    flexFlow: 'column',
    width: '100%',
    overflow: 'auto',
});
